import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Lightbulb } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { NoticeCard } from '@/components/ui/notice-card';
import { api } from '@/lib/utils';
import { useSupport } from '@/lib/support-context';

interface FeedbackPrompt {
  trigger: 'onboarding' | 'import' | 'checkin';
  title: string;
  body: string;
  chips: string[];
}

export function FeedbackPrompt() {
  const qc = useQueryClient();
  const { openSupport } = useSupport();

  const { data } = useQuery({
    queryKey: ['feedback-prompt'],
    queryFn: () => api<{ prompt: FeedbackPrompt | null }>('/api/v1/feedback/prompt'),
    staleTime: 60_000,
  });

  const dismissMutation = useMutation({
    mutationFn: (trigger: FeedbackPrompt['trigger']) =>
      api('/api/v1/feedback/prompt/dismiss', {
        method: 'POST',
        body: JSON.stringify({ trigger }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['feedback-prompt'] }),
  });

  const prompt = data?.prompt;
  if (!prompt) return null;

  const context = `feedback_${prompt.trigger}`;

  return (
    <NoticeCard
      icon={Lightbulb}
      title={prompt.title}
      action={
        <div className="flex flex-wrap items-center gap-2">
          <Button size="sm" onClick={() => openSupport(context, { mode: 'idea' })}>
            <Lightbulb className="h-4 w-4" /> Share an idea
          </Button>
          <Button
            variant="secondary"
            size="sm"
            loading={dismissMutation.isPending}
            onClick={() => dismissMutation.mutate(prompt.trigger)}
          >
            Dismiss
          </Button>
        </div>
      }
    >
      <p>{prompt.body}</p>
      <div className="mt-2 flex flex-wrap gap-2">
        {prompt.chips.map((chip) => (
          <button
            key={chip}
            type="button"
            onClick={() =>
              openSupport(context, {
                mode: 'idea',
                message: chip === 'Something else' ? '' : `I'd like: ${chip.toLowerCase()}. `,
              })
            }
            className="rounded-full border border-border bg-bg-surface px-3 py-1 text-[12px] font-medium text-text-secondary transition-colors hover:border-brand/30 hover:bg-brand-subtle/40 hover:text-text-primary"
          >
            {chip}
          </button>
        ))}
      </div>
    </NoticeCard>
  );
}
